import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Backdrop,
  Divider,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { Close, DragIndicator, MoreVert, Delete } from '@mui/icons-material';
import { useNotification } from '../layouts/Layout';
// MUI ikony i menu nieużywane – usunięte
import AIGeneratePackagesDialog from './AIGeneratePackagesDialog';
import AddPackageForm from './AddPackageForm';
import BulkImportPackagesDialog from './BulkImportPackagesDialog';
import projectService, { Package } from '../../services/projectService';
import { ProjectDisplayData } from '../../pages/ProjectsPage';
import ResponsiblePersonDisplay from './ResponsiblePersonDisplay';

interface PackagesTabProps {
  projectName: string;
  projectData?: ProjectDisplayData;
  onInitiateInquiryCreation?: (packageId: string, packageName: string, projectId: string, projectName: string) => void;
}

// Konwersja danych pakietu z API do formatu używanego w formularzu
const convertApiPackageToFormData = (pkg: Package) => {
  return {
    symbol: pkg.package_symbol,
    name: pkg.name,
    description: pkg.description || '',
    documentationLink: pkg.documentation_link || '',
    packageDocumentationLink: pkg.package_documentation_link || '',
    estimatedValue: pkg.estimated_value ? pkg.estimated_value.toString() : '',
    parameters: pkg.parameters || '',
    stage: pkg.stage,
    package_type: pkg.package_type || '' // Dodanie pola package_type
  };
};

// Konwersja danych formularza do formatu API
const convertFormDataToApiPackage = (formData: any, projectId: string) => {
  return {
    project_id: projectId,
    package_symbol: formData.symbol,
    name: formData.name,
    stage: formData.stage || 'Etap 1',
    description: formData.description,
    documentation_link: formData.documentationLink,
    package_documentation_link: formData.packageDocumentationLink,
    parameters: formData.parameters && formData.parameters.trim() ? formData.parameters.trim() : null,
    estimated_value: formData.estimatedValue ? parseFloat(formData.estimatedValue) : undefined,
    package_type: formData.package_type // Dodanie pola package_type
  };
};

const PackagesTab: React.FC<PackagesTabProps> = ({ projectName, projectData, onInitiateInquiryCreation }) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [openPackageForm, setOpenPackageForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  // Menu akcji usunięte – brak anchorów
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const bulkFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkItems, setBulkItems] = useState<any[]>([]);
  const [openAIDialog, setOpenAIDialog] = useState(false);

  // Tryb zmiany szerokości kolumn (jak w listach projektów)
  const headerCellSx: any = React.useMemo(() => ({
    backgroundColor: (t: any) => (t.palette.mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.06)'),
    position: 'relative'
  }), []);
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number; rightKey?: string; startWRight?: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 180,
    kp: 56,
    name: 250,
    type: 100,
    desc: 200,
    est: 150,
    actions: 160,
  }), []);
  const STORAGE_KEY = React.useMemo(() => 'projectPackagesColWidths', []);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);

  // Konfiguracja widoczności i kolejności kolumn – zgodnie z tabelą projektów
  const ALL_COLUMNS = React.useMemo<string[]>(() => ['id','kp','name','type','desc','est','actions'], []);
  const STORAGE_PREFIX = 'packagesTableCol';
  const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
  const ORDER_KEY = `${STORAGE_PREFIX}:order`;
  const WIDTHS_KEY = `${STORAGE_PREFIX}:widths`;
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) return ord.filter((k:string)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!ord.includes(k)));
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);

  // Panel „Dostosuj kolumny”
  const [colsOpen, setColsOpen] = useState<boolean>(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [colsPosition, setColsPosition] = useState<{ top:number; left:number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = useState<{ startX:number; startY:number; top:number; left:number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 420 } as const;

  // Proste menu dla akcji – tylko "Usuń"
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPkgIndex, setMenuPkgIndex] = useState<number | null>(null);
  const openRowMenu = Boolean(menuAnchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, index: number) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuPkgIndex(index);
  };
  const handleCloseMenu = () => { setMenuAnchorEl(null); setMenuPkgIndex(null); };

  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [colsDragging]);

  // Zapis ustawień
  React.useEffect(() => { try { localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder)); } catch {} }, [ORDER_KEY, columnOrder]);
  React.useEffect(() => { try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible)); } catch {} }, [VISIBLE_KEY, columnsVisible]);
  React.useEffect(() => { try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(columnWidths)); } catch {} }, [WIDTHS_KEY, columnWidths]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, (dragCol.startWRight as number) - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {keyId === 'id' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            // wyznacz prawą, sąsiednią kolumnę (widoczną) – aby zwężać/poszerzać tylko na prawo
            const visOrder = (draftOrder ?? columnOrder).filter((k)=> colV(k));
            const idx = visOrder.indexOf(keyId);
            const rightKey = idx >= 0 && idx < visOrder.length - 1 ? visOrder[idx+1] : undefined;
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: getWidthNum(keyId),
              rightKey,
              startWRight: rightKey ? getWidthNum(rightKey) : undefined
            });
          }}
          sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }}
        />
      ) : null}
    </TableCell>
  );
  // Styl i ikona sortowania jak w liście projektów (dla spójności wizualnej; bez logiki sortowania)
  const headerSortLabelSx = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: '#2D2D32',
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      width: 12,
      height: 12,
      ml: '4px',
      color: '#d4d4d9',
      transform: 'none !important',
      transition: 'none !important'
    },
    '& .MuiTableSortLabel-iconDirectionAsc': {
      transform: 'none !important',
      transition: 'none !important'
    },
    '& .MuiTableSortLabel-iconDirectionDesc': {
      transform: 'none !important',
      transition: 'none !important'
    }
  } as const;
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);

  // Sortowanie
  type SortKey = 'id' | 'kp' | 'name' | 'type' | 'desc' | 'est';
  const [orderBy, setOrderBy] = useState<SortKey>('id');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const getSortLabelProps = (isActive: boolean) => ({
    IconComponent: (isActive ? UnfoldMore : MutedSortIcon) as any,
    active: isActive,
    direction: isActive ? order : 'asc',
    hideSortIcon: false,
    sx: {
      ...headerSortLabelSx,
      '& .MuiTableSortLabel-icon': {
        ...(headerSortLabelSx as any)['& .MuiTableSortLabel-icon'],
        color: isActive ? '#2D2D32' : '#d4d4d9'
      }
    }
  }) as const;
  const handleRequestSort = (property: SortKey) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  const getKpName = React.useCallback((): string => {
    const p: any = (projectData as any)?.responsiblePerson2 || (projectData as any)?.responsible_person2;
    if (!p) return '';
    if (typeof p === 'object' && p !== null) {
      return p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || '';
    }
    if (typeof p === 'string') return p;
    return '';
  }, [projectData]);

  const sortedPackages = React.useMemo(() => {
    const arr = [...packages];
    const cmp = (a: any, b: any) => {
      const dir = order === 'asc' ? 1 : -1;
      switch (orderBy) {
        case 'id': {
          const av = String(a.package_id || '');
          const bv = String(b.package_id || '');
          return av.localeCompare(bv) * dir;
        }
        case 'kp': {
          const av = String((a as any).created_by_name || '');
          const bv = String((b as any).created_by_name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'name': {
          const av = String(a.name || '');
          const bv = String(b.name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'type': {
          const av = String(a.package_type || '');
          const bv = String(b.package_type || '');
          return av.localeCompare(bv) * dir;
        }
        case 'desc': {
          const av = String(a.description || '');
          const bv = String(b.description || '');
          return av.localeCompare(bv) * dir;
        }
        case 'est': {
          const av = Number(a.estimated_value || 0);
          const bv = Number(b.estimated_value || 0);
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    };
    arr.sort(cmp);
    return arr;
  }, [packages, orderBy, order, getKpName]);

  // Pobieranie pakietów z API
  useEffect(() => {
    const fetchPackages = async () => {
      if (!projectData) {
        console.log('Brak danych projektu');
        return;
      }

      // Dodajmy więcej logowania, aby zrozumieć strukturę projectData
      console.log('Dane projektu:', JSON.stringify(projectData, null, 2));
      console.log('Typ project_id:', typeof projectData.project_id);
      console.log('Wartość project_id:', projectData.project_id);

      // Sprawdź, czy mamy prawidłowy identyfikator projektu
      // Jeśli project_id zawiera spację, to jest to złożony identyfikator (np. "224-225 ŁOMŻA DOM KAPITUŁY")
      // W takim przypadku wyciągamy tylko pierwszą część przed spacją
      let projectId = projectData.project_id;
      
      // Sprawdź, czy project_id jest zdefiniowane
      if (!projectId) {
        console.log('project_id jest undefined lub null');
        
        // Sprawdź, czy id jest zdefiniowane i czy można je użyć
        if (projectData.id) {
          console.log('Używam projectData.id zamiast project_id:', projectData.id);
          projectId = projectData.id;
        }
      }
      
      // Jeśli projectId zawiera spację, wyodrębnij pierwszą część
      if (projectId && typeof projectId === 'string' && projectId.includes(' ')) {
        const originalId = projectId;
        projectId = projectId.split(' ')[0];
        console.log(`Wyodrębniony identyfikator projektu: "${projectId}" z "${originalId}"`);
      }

      if (!projectId) {
        const errorMsg = 'Brak identyfikatora projektu';
        console.error(errorMsg);
        setError(errorMsg);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('Pobieranie pakietów dla projektu o ID:', projectId);
        
        // Dodaj obsługę błędów dla przypadku, gdy projectId nie jest stringiem
        if (typeof projectId !== 'string') {
          console.log('Konwersja projectId na string:', String(projectId));
          projectId = String(projectId);
        }
        
        const fetchedPackages = await projectService.getPackagesByProjectId(projectId);
        console.log('Pobrane pakiety:', fetchedPackages);
        
        // Pakiety są już posortowane po package_id malejąco przez backend
        setPackages(fetchedPackages);
      } catch (err: any) {
        console.error('Błąd podczas pobierania pakietów:', err);
        if (err.response) {
          console.error('Odpowiedź serwera:', err.response.data);
          console.error('Status błędu:', err.response.status);
        }
        setError(err.message || 'Wystąpił błąd podczas pobierania pakietów');
        // Jeśli wystąpił błąd, używamy pustej tablicy
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [projectData]);

  // Funkcje dla pakietów
  const handleOpenPackageForm = () => {
    setEditMode(false);
    setSelectedPackage(null);
    setOpenPackageForm(true);
  };

  const handleOpenEditPackageForm = () => {
    if (selectedItems.length === 1) {
      const packageToEdit = packages[selectedItems[0]];
      if (packageToEdit) {
        setSelectedPackage(convertApiPackageToFormData(packageToEdit));
        setEditMode(true);
        setOpenPackageForm(true);
      }
    }
  };

  const handleClosePackageForm = () => {
    setOpenPackageForm(false);
    setEditMode(false);
    setSelectedPackage(null);
  };

  const handleAddPackage = async (newPackageData: any, files?: File[]) => {
    if (!projectData) {
      showNotification('Nie można dodać pakietu - brak danych projektu', 'error');
      return;
    }

    // Wyodrębnij prawidłowy identyfikator projektu
    let projectId = projectData.project_id;
    
    // Sprawdź, czy project_id jest zdefiniowane
    if (!projectId) {
      console.log('project_id jest undefined lub null przy dodawaniu pakietu');
      
      // Sprawdź, czy id jest zdefiniowane i czy można je użyć
      if (projectData.id) {
        console.log('Używam projectData.id zamiast project_id przy dodawaniu pakietu:', projectData.id);
        projectId = projectData.id;
      }
    }
    
    // Jeśli projectId zawiera spację, wyodrębnij pierwszą część
    if (projectId && typeof projectId === 'string' && projectId.includes(' ')) {
      const originalId = projectId;
      projectId = projectId.split(' ')[0];
      console.log(`Wyodrębniony identyfikator projektu przy dodawaniu pakietu: "${projectId}" z "${originalId}"`);
    }

    if (!projectId) {
      showNotification('Nie można dodać pakietu - brak ID projektu', 'error');
      return;
    }

    // Dodaj obsługę błędów dla przypadku, gdy projectId nie jest stringiem
    if (typeof projectId !== 'string') {
      console.log('Konwersja projectId na string przy dodawaniu pakietu:', String(projectId));
      projectId = String(projectId);
    }

    try {
      setLoading(true);
      console.log('Tworzenie pakietu dla projektu o ID:', projectId);
      const apiPackageData = convertFormDataToApiPackage(newPackageData, projectId);
      // Przekaż ewentualne files_paths z importu JSON do backendu (bez wyboru plików w UI)
      const payload: any = { ...apiPackageData };
      if ((newPackageData as any)?.files_paths && Array.isArray((newPackageData as any).files_paths)) {
        payload.files_paths = (newPackageData as any).files_paths;
      }
      // Dodaj pliki do danych pakietu, jeśli istnieją
      const newPackage = await projectService.createPackage({
        ...payload,
        files: files
      } as any);
      
      setPackages([...packages, newPackage]);
      showNotification('Pakiet został dodany pomyślnie', 'success');
    } catch (err: any) {
      console.error('Błąd podczas dodawania pakietu:', err);
      showNotification(err.message || 'Wystąpił błąd podczas dodawania pakietu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPackage = async (editedPackageData: any, files?: File[]) => {
    if (selectedItems.length !== 1) {
      return;
    }

    const packageToEdit = packages[selectedItems[0]];
    if (!packageToEdit || !packageToEdit.package_id) {
      showNotification('Nie można edytować pakietu - brak ID pakietu', 'error');
      return;
    }

    try {
      setLoading(true);
      const apiPackageData = convertFormDataToApiPackage(editedPackageData, packageToEdit.project_id);
      
      // Dodaj pliki do danych pakietu, jeśli istnieją
      const updatedPackage = await projectService.updatePackage(
        packageToEdit.package_id, 
        {
          ...apiPackageData,
          files: files
        }
      );
      
      const updatedPackages = packages.map((pkg, index) => 
        selectedItems[0] === index ? updatedPackage : pkg
      );
      
      setPackages(updatedPackages);
      setSelectedItems([]);
      showNotification('Pakiet został zaktualizowany pomyślnie', 'success');
    } catch (err: any) {
      console.error('Błąd podczas aktualizacji pakietu:', err);
      showNotification(err.message || 'Wystąpił błąd podczas aktualizacji pakietu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async (packageSymbol: string) => {
    if (selectedItems.length !== 1) {
      return;
    }

    const packageToDelete = packages[selectedItems[0]];
    if (!packageToDelete || !packageToDelete.package_id) {
      showNotification('Nie można usunąć pakietu - brak ID pakietu', 'error');
      return;
    }

    try {
      setLoading(true);
      const success = await projectService.deletePackage(packageToDelete.package_id);
      
      if (success) {
        const updatedPackages = packages.filter((_, index) => index !== selectedItems[0]);
        setPackages(updatedPackages);
        setSelectedItems([]);
        showNotification('Pakiet został usunięty pomyślnie', 'success');
      } else {
        throw new Error('Nie udało się usunąć pakietu');
      }
    } catch (err: any) {
      console.error('Błąd podczas usuwania pakietu:', err);
      showNotification(err.message || 'Wystąpił błąd podczas usuwania pakietu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (index: number) => {
    const selectedIndex = selectedItems.indexOf(index);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItems, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1),
      );
    }

    setSelectedItems(newSelected);
  };

  const handleSelectAllItems = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = Array.from({ length: packages.length }, (_, i) => i);
      setSelectedItems(newSelecteds);
      return;
    }
    setSelectedItems([]);
  };

  const isItemSelected = (index: number) => selectedItems.indexOf(index) !== -1;


  // Obsługa menu akcji
  // Funkcje menu usunięte

  // Pomocniczo: zwróć poprawne project_id (bez części nazwy po spacji)
  const getNormalizedProjectId = (): string | null => {
    if (!projectData) return null;
    let projectId: any = projectData.project_id || projectData.id || null;
    if (!projectId) return null;
    if (typeof projectId !== 'string') projectId = String(projectId);
    if (projectId.includes(' ')) {
      projectId = projectId.split(' ')[0];
    }
    return projectId;
  };

  // Pobierz szablon JSON do importu pakietu
  const handleDownloadJsonTemplate = () => {
    const template = {
      package_symbol: 'SYM',
      name: 'Nazwa pakietu',
      stage: 'Etap 1',
      package_type: 'materiały',
      description: '',
      parameters: '',
      estimated_value: 0
    } as const;
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'package-import-template.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Kliknięcie importu JSON
  const handleImportJsonClick = () => {
    if (!fileInputRef.current) {
      fileInputRef.current = document.createElement('input');
      fileInputRef.current.type = 'file';
      fileInputRef.current.accept = 'application/json,.json';
      fileInputRef.current.onchange = handleJsonFileSelected as any;
    }
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  // Zbiorczy import – wczytanie pliku JSON (tablica)
  const handleBulkImportClick = () => {
    if (!bulkFileInputRef.current) {
      bulkFileInputRef.current = document.createElement('input');
      bulkFileInputRef.current.type = 'file';
      bulkFileInputRef.current.accept = 'application/json,.json';
      bulkFileInputRef.current.onchange = async (e: any) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        try {
          const text = await file.text();
          const json = JSON.parse(text);
          const list = Array.isArray(json) ? json : [json];
          if (list.length === 0) return;
          setBulkItems(list);
          setBulkDialogOpen(true);
        } catch (err) {
          showNotification('Nie udało się odczytać pliku JSON (zbiorczy)', 'error');
        }
      };
    }
    bulkFileInputRef.current.value = '';
    bulkFileInputRef.current.click();
  };

  // Wczytanie i przetworzenie pliku JSON
  const handleJsonFileSelected = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const item = Array.isArray(json) ? (json[0] || {}) : json;
      // Minimalna walidacja wejścia
      if (!item || typeof item !== 'object') {
        showNotification('Plik JSON nie zawiera poprawnych danych pakietu', 'error');
        return;
      }

      // Zmapuj pola JSON na strukturę formularza AddPackageForm
      const formData = {
        symbol: item.package_symbol || item.symbol || '',
        name: item.name || '',
        description: item.description || '',
        documentationLink: item.documentation_link || '',
        packageDocumentationLink: item.package_documentation_link || '',
        estimatedValue: item.estimated_value !== undefined && item.estimated_value !== null
          ? String(item.estimated_value)
          : '',
        parameters: item.parameters || '',
        stage: item.stage || 'Etap 1',
        package_type: item.package_type || ''
      };

      // Ostrzeż o niewspieranych ścieżkach plików
      if (item.files_paths && Array.isArray(item.files_paths) && item.files_paths.length > 0) {
        showNotification('Ścieżki plików w JSON są informacyjne – wgraj pliki ręcznie w formularzu.', 'warning');
      }

      // Otwórz formularz dodawania z uzupełnionymi danymi
      // Zachowaj ewentualne ścieżki plików z JSON (do przekazania do formularza)
      const filesPaths: string[] = Array.isArray(item.files_paths) ? item.files_paths : [];

      setSelectedPackage({ ...formData, __files_paths: filesPaths });
      setEditMode(false);
      setOpenPackageForm(true);
      showNotification('Wczytano dane z JSON. Sprawdź i zapisz pakiet.', 'info');
    } catch (err: any) {
      console.error('Błąd odczytu pliku JSON:', err);
      showNotification('Nie udało się odczytać lub przetworzyć pliku JSON', 'error');
    }
  };

  // handleMenuAction usunięte

  // Akcje w wierszu – bez rozwijanego menu
  const handleRowAction = (action: 'edit' | 'delete' | 'openDocumentation' | 'openPackageDocumentation' | 'createInquiry', currentPackage: Package) => {
    if (action === 'edit') {
      setSelectedPackage(convertApiPackageToFormData(currentPackage));
      setEditMode(true);
      setOpenPackageForm(true);
      return;
    }
    if (action === 'delete') {
      handleDeletePackage(currentPackage.package_symbol);
      return;
    }
    if (action === 'openDocumentation') {
      if ((currentPackage as any).documentation_link) {
        window.open((currentPackage as any).documentation_link, '_blank');
      } else {
        showNotification('Link do dokumentacji nie jest jeszcze dostępny', 'info');
      }
      return;
    }
    if (action === 'openPackageDocumentation') {
      if ((currentPackage as any).package_documentation_link) {
        window.open((currentPackage as any).package_documentation_link, '_blank');
      } else {
        showNotification('Link do dokumentacji pakietu nie jest jeszcze dostępny', 'info');
      }
      return;
    }
    if (action === 'createInquiry') {
      if (onInitiateInquiryCreation && projectData) {
        let pId: any = projectData.project_id || projectData.id || '';
        if (typeof pId !== 'string') pId = String(pId);
        if (pId.includes(' ') && !pId.startsWith('226-227')) pId = pId.split(' ')[0];
        onInitiateInquiryCreation((currentPackage as any).package_id, (currentPackage as any).name, pId, projectName);
      }
      return;
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>

      {/* Pasek akcji – nad tabelą, wyrównany do prawej, nie zmienia wysokości układu */}
      <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
        {/* Grupa akcji */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Generuj pakiety z przedmiarów">
            <span>
              <IconButton onClick={() => setOpenAIDialog(true)} disabled={loading || !projectData}>
                <Box component="img" src="/assets/icons/ai.svg" alt="AI" sx={{ width: 22, height: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Pobierz szablon JSON">
            <span>
              <IconButton onClick={handleDownloadJsonTemplate} disabled={loading || !projectData}>
                <Box component="img" src="/assets/icons/json.svg" alt="Pobierz" sx={{ width: 22, height: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zbiorczy import z JSON">
            <span>
              <IconButton onClick={handleBulkImportClick} disabled={loading || !projectData}>
                <Box component="img" src="/assets/icons/upload.svg" alt="Import" sx={{ width: 22, height: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Dodaj pakiet">
            <span>
              <IconButton onClick={handleOpenPackageForm} disabled={loading || !projectData} data-hotkey-add="true">
                <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* Odstęp 16px między grupami ikon */}
        <Box sx={{ width: 16 }} />
        {/* Dostosuj kolumny – ostatnia ikona, na prawej krawędzi jak w /projects */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              alignSelf: 'flex-end',
              bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={() => { setColResizeMode(true); setColsOpen(true); setColsPosition({ top: 0, left: 0 }); }}
          >
            <Box
              component="img"
              alt="kolumny"
              src={'/assets/icons/frame-344.svg?v=2'}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>
      
      {/* Tabela pakietów */}
      <Box sx={{ position: 'relative' }}>
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mt: 0,
          height: 'auto',
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d0d0d6',
          borderBottom: '1px solid #d0d0d6',
          borderRadius: 0,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          overflowY: 'auto',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, color: 'error.main' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        ) : (
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
              '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
              '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
              '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
            }}
          >
            {(() => {
              const ord = (draftOrder ?? columnOrder);
              return (
                <colgroup>
                  {ord.filter((k)=>k!=='actions').map((key) => (
                    colV(key) ? <col key={`col-${key}`} style={{ width: getColW(key) }} /> : null
                  ))}
                  {colV('actions') ? <col key="col-actions" style={{ width: getColW('actions') }} /> : null}
                </colgroup>
              );
            })()}
            <TableHead>
              <TableRow>
                {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions').map((key) => colV(key) ? renderResizableTh(key, (
                  key === 'id' ? (
                  <Box sx={{ pl: '16px', display: 'inline-flex', alignItems: 'center' }}>
                    <TableSortLabel {...getSortLabelProps(orderBy === 'id')} onClick={() => handleRequestSort('id')}>
                      ID pakietu
                    </TableSortLabel>
                  </Box>
                  ) : key === 'kp' ? (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'kp')} onClick={() => handleRequestSort('kp')}>
                    KP
                  </TableSortLabel>
                  ) : key === 'name' ? (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'name')} onClick={() => handleRequestSort('name')}>
                    Nazwa pakietu
                  </TableSortLabel>
                  ) : key === 'type' ? (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'type')} onClick={() => handleRequestSort('type')}>
                    Rodzaj
                  </TableSortLabel>
                  ) : key === 'desc' ? (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'desc')} onClick={() => handleRequestSort('desc')}>
                    Opis
                  </TableSortLabel>
                  ) : key === 'est' ? (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'est')} onClick={() => handleRequestSort('est')}>
                    Estymacja
                  </TableSortLabel>
                  ) : (
                    ''
                  )
                ), key === 'est' ? 'right' : key === 'kp' ? 'center' : undefined) : null)}
                {colV('actions') ? renderResizableTh('actions', '', 'center') : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" sx={{ py: 2 }}>
                      Brak pakietów dla tego projektu
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedPackages.map((pkg, index) => {
                  return (
                    <TableRow 
                      key={pkg.package_id} 
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: '#F5F5FF !important' },
                        '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
                        '&.Mui-selected': { backgroundColor: '#F5F5FF !important' },
                        '&.Mui-selected:hover': { backgroundColor: '#F5F5FF !important' }
                      }}
                    >
                      {colV('id') ? (
                        <TableCell sx={{ width: getColW('id') }}>
                          <Box sx={{ pl: '16px' }}>{pkg.package_id}</Box>
                        </TableCell>
                      ) : null}
                      {colV('kp') ? (
                      <TableCell align="center" sx={{ width: getColW('kp') }}>
                        {((pkg as any).created_by_avatar_url || (pkg as any).created_by_name) ? (
                          <Tooltip title={(pkg as any).created_by_name || ''} arrow>
                            <Avatar src={(pkg as any).created_by_avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                              {((pkg as any).created_by_name || '').substring(0,1)}
                            </Avatar>
                          </Tooltip>
                        ) : (
                          <Avatar sx={{ width: 25, height: 25, fontSize: 12 }}>–</Avatar>
                        )}
                      </TableCell>
                      ) : null}
                      {colV('name') ? (<TableCell sx={{ width: getColW('name') }}>{pkg.name}</TableCell>) : null}
                      {colV('type') ? (<TableCell sx={{ width: getColW('type') }}>{pkg.package_type || '-'}</TableCell>) : null}
                      {colV('desc') ? (
                        <TableCell sx={{ width: getColW('desc') }}>
                          <Box sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal'
                          }}>
                            {pkg.description}
                          </Box>
                        </TableCell>
                      ) : null}
                      {colV('est') ? (<TableCell align="right" sx={{ width: getColW('est') }}>
                        {pkg.estimated_value 
                          ? (pkg.estimated_value / 1000).toLocaleString('pl-PL')
                          : '-'}
                      </TableCell>) : null}
                      {colV('actions') ? (<TableCell align="center" sx={{ width: getColW('actions') }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          <Tooltip title="Dokumentacja pakietu" arrow>
                            <span>
                              <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); handleRowAction('openPackageDocumentation', pkg as any); }}>
                                <Box component="img" src="/assets/icons/package.svg" alt="Dokumentacja pakietu" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Dokumentacja całkowita" arrow>
                            <span>
                              <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); handleRowAction('openDocumentation', pkg as any); }}>
                                <Box component="img" src="/assets/icons/folder.svg" alt="Dokumentacja całkowita" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Utwórz zapytanie" arrow>
                            <span>
                              <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); handleRowAction('createInquiry', pkg as any); }}>
                                <Box component="img" src="/assets/icons/inquiry.svg" alt="Utwórz zapytanie" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" onClick={(e)=> handleOpenMenu(e, index)} aria-label="więcej">
                            <MoreVert fontSize="small" sx={{ color: '#2D2D32' }} />
                          </IconButton>
                        </Box>
                      </TableCell>) : null}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      </Box>

      {/* Okno „Dostosuj kolumny” – spójne z tabelą projektów */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)' }} onClick={()=> setColsOpen(false)}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${COLUMNS_DIALOG.height}px`,
            width: `${COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{(key === 'id' ? 'ID pakietu' : key === 'name' ? 'Nazwa pakietu' : key === 'type' ? 'Rodzaj' : key === 'desc' ? 'Opis' : key === 'est' ? 'Estymacja' : key.toUpperCase())}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ if (draftColumnsVisible) { setColumnsVisible(draftColumnsVisible); try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible)); } catch {} } if (draftOrder) { const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); setColumnOrder(normalized); try { localStorage.setItem(ORDER_KEY, JSON.stringify(normalized)); } catch {} } setColsOpen(false); setColResizeMode(false); }}>
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Menu akcji w wierszu – tylko Usuń */}
      <Menu
        anchorEl={menuAnchorEl}
        open={openRowMenu}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => { 
          const idx = menuPkgIndex ?? -1; 
          if (idx >= 0) { 
            const current = sortedPackages[idx]; 
            handleRowAction('edit', current as any); 
          } 
          handleCloseMenu();
        }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <Box component="img" src="/assets/icons/edit2.svg" alt="Edytuj" sx={{ width: 18, height: 18 }} />
          </ListItemIcon>
          <ListItemText primary="Edytuj" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
        </MenuItem>
        <MenuItem onClick={() => { 
          const idx = menuPkgIndex ?? -1; 
          if (idx >= 0) { 
            const current = sortedPackages[idx]; 
            handleRowAction('delete', current as any); 
          } 
          handleCloseMenu();
        }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <Box component="img" src="/assets/icons/delete2.svg" alt="Usuń" sx={{ width: 18, height: 18 }} />
          </ListItemIcon>
          <ListItemText primary="Usuń" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
        </MenuItem>
      </Menu>

      <AddPackageForm
        open={openPackageForm}
        onClose={handleClosePackageForm}
        onAdd={handleAddPackage}
        onEdit={handleEditPackage}
        onDelete={handleDeletePackage}
        editMode={editMode}
        packageData={selectedPackage}
        importedFilePaths={selectedPackage?.__files_paths || []}
      />

      <BulkImportPackagesDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        projectId={(projectData?.project_id || projectData?.id || '').toString().split(' ')[0]}
        initialPackages={bulkItems}
        onCompleted={(summary) => {
          if (projectData) {
            const pid = (projectData.project_id || projectData.id || '').toString().split(' ')[0];
            projectService.getPackagesByProjectId(pid).then(setPackages);
          }
          // Pokazanie monitu końcowego
          const { successes = 0, failures = 0, total = 0 } = summary || ({} as any);
          const msg = `Import zakończony. Utworzono: ${successes}/${total}${failures ? `, błędy: ${failures}` : ''}.`;
          showNotification(msg, failures ? 'warning' : 'success');
        }}
      />

      <AIGeneratePackagesDialog
        open={openAIDialog}
        onClose={() => setOpenAIDialog(false)}
        onGenerated={(items)=>{
          // Otwórz zbiorczy import z gotowymi elementami
          setBulkItems(Array.isArray(items) ? items : [items]);
          setOpenAIDialog(false);
          setBulkDialogOpen(true);
        }}
      />
    </Box>
  );
};

export default PackagesTab;
